/**
 * Client-side visitor attribution.
 *
 * Captures FIRST-TOUCH referrer + campaign params on the visitor's first page
 * load and persists them in a cookie, so that by the time they submit a form
 * (often after navigating internally to /contact) we still know how they
 * originally arrived. Used to stamp lead submissions with `page`, a friendly
 * `referral_type` channel label, and the raw referrer / UTM details.
 */

const ATTRIBUTION_COOKIE = 'ps_attribution';
const COOKIE_MAX_AGE_DAYS = 90;

export interface Attribution {
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  gclid: string;
  fbclid: string;
  landing: string;
}

/** Fields appended to a form submission's FormData. */
export interface SubmissionContext extends Attribution {
  page: string;
  referral_type: string;
  locale: string;
}

const VALID_LOCALES = ['en-us', 'zh-cn', 'zh-hk'];

/** The locale segment the visitor was browsing, from the URL path. */
function currentLocale(): string {
  if (typeof window === 'undefined') return '';
  const seg = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase() || '';
  return VALID_LOCALES.includes(seg) ? seg : '';
}

const EMPTY_ATTRIBUTION: Attribution = {
  referrer: '',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  utm_content: '',
  gclid: '',
  fbclid: '',
  landing: '',
};

// Domains used to classify an organic referrer.
const SEARCH_ENGINES = [
  'google.',
  'bing.',
  'yahoo.',
  'duckduckgo.',
  'baidu.',
  'yandex.',
  'ecosia.',
  'sogou.',
];

const SOCIAL_NETWORKS = [
  'facebook.',
  'fb.com',
  'm.facebook',
  'linkedin.',
  'lnkd.in',
  't.co',
  'twitter.',
  'x.com',
  'instagram.',
  'youtube.',
  'youtu.be',
  'reddit.',
  'pinterest.',
  'tiktok.',
  'weibo.',
];

function readCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
}

function writeCookie(name: string, value: string, maxAgeDays: number): void {
  if (typeof document === 'undefined') return;
  const maxAge = maxAgeDays * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/** Read attribution from the current URL + document.referrer (last-touch). */
function liveAttribution(): Attribution {
  if (typeof window === 'undefined') return { ...EMPTY_ATTRIBUTION };

  const params = new URLSearchParams(window.location.search);

  // Ignore same-host referrers — those are internal navigations, not a source.
  let referrer = document.referrer || '';
  try {
    if (referrer && new URL(referrer).host === window.location.host) {
      referrer = '';
    }
  } catch {
    referrer = '';
  }

  return {
    referrer,
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    gclid: params.get('gclid') || '',
    fbclid: params.get('fbclid') || '',
    landing: window.location.pathname,
  };
}

/**
 * Persist the first-touch attribution on the very first page load.
 * No-ops if a cookie already exists (first touch wins) or off the client.
 */
export function captureFirstTouchAttribution(): void {
  if (typeof window === 'undefined') return;
  if (readCookie(ATTRIBUTION_COOKIE)) return;

  try {
    writeCookie(ATTRIBUTION_COOKIE, JSON.stringify(liveAttribution()), COOKIE_MAX_AGE_DAYS);
  } catch {
    // Cookies disabled — fall back to last-touch at submit time.
  }
}

function getStoredAttribution(): Attribution {
  const raw = readCookie(ATTRIBUTION_COOKIE);
  if (raw) {
    try {
      return { ...EMPTY_ATTRIBUTION, ...(JSON.parse(raw) as Partial<Attribution>) };
    } catch {
      // Malformed cookie — fall through to live values.
    }
  }
  return liveAttribution();
}

function hostMatches(referrer: string, needles: string[]): boolean {
  if (!referrer) return false;
  let host = '';
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return false;
  }
  return needles.some((n) => host.includes(n));
}

/** Derive a human-friendly marketing channel from attribution signals. */
export function classifyChannel(attr: Attribution): string {
  const medium = attr.utm_medium.toLowerCase();
  const source = attr.utm_source.toLowerCase();

  // Paid search — Google Ads click id or a paid-search medium.
  if (attr.gclid || ['cpc', 'ppc', 'paidsearch', 'paid-search', 'sem'].includes(medium)) {
    return 'Paid Search';
  }

  // Paid social — Meta click id or an explicit paid-social medium.
  if (
    attr.fbclid ||
    ['paidsocial', 'paid-social', 'social-paid', 'cpm'].includes(medium) ||
    (medium.includes('paid') && SOCIAL_NETWORKS.some((n) => source.includes(n.replace('.', ''))))
  ) {
    return 'Paid Social';
  }

  if (medium === 'email' || ['email', 'newsletter'].includes(source)) {
    return 'Email';
  }

  if (['social', 'social-organic', 'organic-social'].includes(medium)) {
    return 'Social';
  }

  // Any other tagged medium (referral, affiliate, partner, display, …).
  if (medium) {
    return medium.charAt(0).toUpperCase() + medium.slice(1);
  }

  // Fall back to referrer host classification.
  if (hostMatches(attr.referrer, SEARCH_ENGINES)) return 'Organic Search';
  if (hostMatches(attr.referrer, SOCIAL_NETWORKS)) return 'Social';
  if (attr.referrer) return 'Referral';

  return 'Direct';
}

/**
 * Build the context appended to a form submission: the current page, the
 * derived referral channel, and the raw first-touch attribution fields.
 */
export function getSubmissionContext(): SubmissionContext {
  const attr = getStoredAttribution();
  // Path only (domain stripped); keep search + hash so we know the exact
  // page/anchor the form was submitted from, e.g. "/en-us/implementations/#contact".
  const page =
    typeof window !== 'undefined'
      ? window.location.pathname + window.location.search + window.location.hash
      : '';
  return {
    ...attr,
    page,
    referral_type: classifyChannel(attr),
    locale: currentLocale(),
  };
}
